import React, { useState, useEffect } from 'react';
import { oneLine } from 'common-tags';

import { Modal } from '../../themes/nzf_2022';
import CloseModal from './close-button';
import Notification from './notification';


const TicketDescription = ({ selectedTicket, settings }) => {
  if (selectedTicket) {
    return (
      <div className={`${selectedTicket && selectedTicket.description.icon === 'heart' ? 'bg-red-5 pb-3' : 'bg-transparent'} flex flex-column flex-ai-center mx-auto mt-3 sm+:mt-5`}>
        <img
          src={`/static/images/${selectedTicket.description.icon}.png`}
          alt={selectedTicket.description.icon}
          className='mt-6'
          style={{ maxWidth: '46px' }}
        />
        <p className='text-center mt-2 max-w-4/5'>{selectedTicket.description.text}</p>
      </div>
    );
  }
  return (
    <div className='flex flex-column flex-ai-center mx-auto max-w-10/12 pt-12'>
      <p className='text-center'>{settings.default_ticket_blurb}</p>
    </div>
  );
};

const TicketQuantity = ({ sessionConfig, numTickets, handleQuantityChange, ticketsPrice, selectedTicket }) => {
  let maxTickets = sessionConfig.max_tickets;
  let remainingTickets = maxTickets;
  if (selectedTicket) {
    if (selectedTicket.remaining < maxTickets && selectedTicket.remaining !== null) {
      maxTickets = selectedTicket.remaining;
    }
    remainingTickets = selectedTicket.remaining;
  }
  let quantityMessage;
  if (remainingTickets >= 1 && remainingTickets <= 10) {
    quantityMessage = `There ${remainingTickets > 1 ? 'are only' : 'is'} ${remainingTickets} ticket${remainingTickets > 1 ? 's' : ''} left at this price.`;
  } else if (remainingTickets < 1) {
    quantityMessage = 'Sorry, there are no more tickets available at this price.';
  } else {
    // keep quantityMessage empty
  }

  return (
    <>
      <div className='flex flex-row mt-6 w-11/12 sm+:w-8/12'>
        <div className='flex flex-column w-6/12'>
          <div className='mr-auto'>
            <h4 className='font-normal text-base'>Quantity</h4>
            <input
              type='number'
              inputMode='decimal'
              min='0'
              max={maxTickets}
              value={numTickets}
              onChange={(e) => {
                handleQuantityChange(e);
              }}
              className='ticket-input-quantity mt-4'
              onKeyPress={(e) => {
                if (e.charCode === 38) { // increase, up arrow
                  handleQuantityChange(e);
                } else if (e.charCode === 40) { // decrease, down arrow
                  handleQuantityChange(e);
                }
              }}
            />
          </div>
          {(!selectedTicket) ? (
            <div>
              <p className='color-grey-20'>Select a ticket price above</p>
            </div>
          ) : null}
        </div>

        <div className='flex flex-column w-6/12'>
          <div className='ml-auto ticket-width-wrapper'>
            <h4 className='font-normal text-base mb-6 mr-10 sm+:mr-0'>Total</h4>
            <span className='font-medium text-xl ticket-input-tickets-price mr-10 sm+:mr-0'>
              $
              {ticketsPrice}
            </span>
          </div>
        </div>
      </div>

      {/* errors */}
      {quantityMessage && (
        <div className='w-11/12 sm+:w-8/12'>
          <p className='pt-3 h4 font-normal text-sm sm+:text-base color-grey mr-auto'>{quantityMessage}</p>
        </div>
      )}
      {!quantityMessage && (
        <div className='w-11/12 sm+:w-8/12 mb-8'>{ /* spaceholder to prevent ui jumping */}</div>
      )}
    </>
  );
};

const TicketSelection = ({ eventName, tickets, settings, session, sessionConfig, handleTicketPurchase, ticketPurchaseError }) => {
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [numTickets, setNumTickets] = useState(0);
  const [ticketsPrice, setTicketsPrice] = useState(0);

  const handleSelection = (ticket) => {
    setSelectedTicket(ticket);
    setButtonDisabled(false);

    if (ticket.remaining > 0) {
      // Reset quantities back to 1.
      setNumTickets(1);
      setTicketsPrice(1 * ticket.ticket_price);
    } else {
      setNumTickets(0);
      setTicketsPrice(0);
      setButtonDisabled(true);
    }
  };


  useEffect(() => {
    const ticket = tickets.find(ticket => ticket.is_recommended_price);
    if (ticket !== undefined) {
      handleSelection(ticket);
      if (tickets.length > 1) {
        positionFlagAboveReccomendedPrice(
          '[data-recommended-price="true"]',
          '.recommended-price-flag',
          '.modal__container',
          0
        );
      }
    }
  }, []);

  /* position recommended price flag on correct ticket item */
  const positionFlagAboveReccomendedPrice = (referenceSelector, positionedSelector, modalSelector, padding) => {
    const modal = document.querySelector(modalSelector);
    const refElement = modal.querySelector(referenceSelector);
    const positionedElement = modal.querySelector(positionedSelector);

    const refRect = refElement.getBoundingClientRect();
    const modalRect = modal.getBoundingClientRect();

    const positionedRect = positionedElement.getBoundingClientRect();
    const positionedTop = refRect.top - modalRect.top - positionedRect.height;
    const positionedLeft = refRect.left - modalRect.left + (refRect.width / 2) - (positionedRect.width / 2) - padding;

    positionedElement.style.top = `${positionedTop}px`;
    positionedElement.style.left = `${positionedLeft}px`;
  };

  const showSoldOutAnimation = (e) => {
    e.target.classList.add('sold-out-animation');
    setTimeout(() => {
      e.target.classList.remove('sold-out-animation');
    }, 1000);
  };

  const handleQuantityChange = (e) => {
    const quantity = parseInt(e.target.value, 10);
    if (Number.isNaN(quantity)) {
      setButtonDisabled(true);
      setNumTickets('');
    } else {
      let maxTickets = sessionConfig.max_tickets;
      if (selectedTicket) {
        if (selectedTicket.remaining < maxTickets) {
          maxTickets = selectedTicket.remaining;
        }
      }
      if (quantity >= 1 && quantity <= maxTickets) {
        setNumTickets(quantity);
        setButtonDisabled(false);
        if (selectedTicket) {
          setTicketsPrice(quantity * selectedTicket.ticket_price);
        }
      } else if (quantity <= 0) {
        setButtonDisabled(true);
        setNumTickets(0);
      }
    }
  };

  const title = (tickets.length > 1) ? 'Set your ticket price' : 'Set your number of tickets';

  return (
    <div className='flex flex-column flex-ai-center pt-12 sm+:pt-20 pb-16 w-11/12 mx-auto cyp-modal-inner'>
      {ticketPurchaseError ? (
        <Notification errorMessage={ticketPurchaseError} />
      ) : null}
      <h3
        id='price-title'
        className='w-8/12 sm+:w-11/12 text-center mx-auto u-text-no-transform'
      >
        {title}
      </h3>
      <h5 className='mt-4 mb-10 h5-sussie u-text-no-transform'>
        {eventName}
        {session.start_date_human ? `, ${session.start_date_human}` : ''}
        {' '}
        {session.start_time_human ? `, ${session.start_time_human}` : ''}

      </h5>
      {tickets.length > 1 && (
        <div className='w-12/12'>
          {tickets.map((ticket) => (
            <div key={ticket.id}>
              {ticket.is_recommended_price
                ? (
                  <div className='recommended-price-flag absolute'>
                    <div className='flex'>
                      <div>
                        <div className='bg-grey-10 p3 m0'>Recommended price</div>
                        <div className='flex flex-jc-center'>
                          <span className='recommended-price-flag__triangle' />
                        </div>
                      </div>
                    </div>
                  </div>
                )
                : null}
            </div>
          ))}
          <ul
            className='flex px-0 flex-row border2 border-solid border-grey-20 mx-auto m0 mt-12 relative'
            role='radiogroup'
          >
            {tickets.map((ticket) => (
              <li
                key={ticket.id}
                onClick={(e) => {
                  if (ticket.sold_out) {
                    e.persist();
                    showSoldOutAnimation(e);
                  } else {
                    handleSelection(ticket);
                  }
                }}
                onKeyPress={(e) => {
                  if (e.charCode === 32) { // spacebar
                    if (ticket.sold_out) {
                      e.persist();
                      showSoldOutAnimation(e);
                    } else {
                      handleSelection(ticket);
                    }
                  }
                }}
                data-recommended-price={ticket.is_recommended_price}
                role='radio'
                tabIndex='0' // hack to make more like a radio btn
                aria-checked={(selectedTicket && selectedTicket.id === ticket.id) ? 'true' : 'false'}
                aria-labelledby='price-title'
                className={oneLine`tickets-list-item m0 py-4 flex-grow-1 flex flex-jc-center font-medium sm+:px-10
                   ${ /* bg-color options */''}
                   ${ticket.sold_out ? 'tickets-list-item-sold-out cursor-not-allowed' : `${selectedTicket && selectedTicket.id === ticket.id ? 'bg-black color-white' : 'bg-white color-black'} cursor-pointer`}
                   ${ /* show/hide the arrow after element that shows below this element */''}
                   ${selectedTicket && selectedTicket.id === ticket.id && selectedTicket.description.icon === 'heart' ? 'ticket-price-arrow' : ''}`}
              >
                {/* <span className='px-10 font-medium'>{`$${ ticket.ticket_price } `}</span> */}
                {`$${ticket.ticket_price} `}
              </li>
            ))}
          </ul>

          <TicketDescription
            selectedTicket={selectedTicket}
            settings={settings}
          />

        </div>
      )}
      {selectedTicket ? (
        <>
          <TicketQuantity
            sessionConfig={sessionConfig}
            numTickets={numTickets}
            ticketsPrice={ticketsPrice}
            handleQuantityChange={handleQuantityChange}
            selectedTicket={selectedTicket}
          />
          <button
            disabled={buttonDisabled}
            type='button'
            className='btn btn--default btn--large w-11/12 mx-auto px-12 py-4 text-sm sm+:px-3 sm+:max-w-8/12 whitespace-normal mt-12'
            onClick={() => handleTicketPurchase(numTickets, selectedTicket)}
          >
            Continue
          </button>
        </>
      ) : null}

    </div>
  );
};

const TicketSelectionModal = ({ closeModal, eventName, tickets, settings, session, sessionConfig, handleTicketPurchase, ticketPurchaseError }) => {
  const onClose = () => {
    closeModal();
  };
  return (
    <Modal
      onCloseCB={onClose}
      parsedContent={(
        <>
          <CloseModal />
          <TicketSelection
            eventName={eventName}
            tickets={tickets}
            session={session}
            sessionConfig={sessionConfig}
            settings={settings}
            handleTicketPurchase={handleTicketPurchase}
            ticketPurchaseError={ticketPurchaseError}
          />
        </>
      )}
    />
  );
};

export default TicketSelectionModal;
